import React, { useState, useEffect } from 'react';
import { Download, Smartphone, ShieldCheck, HelpCircle, ArrowRight, Languages, ChevronLeft, ChevronRight, Award, Flame, Play, Volume2 } from 'lucide-react';

const DownloadPage = () => {
    // Determine initial language from local storage, geolocation, or default
    const [lang, setLang] = useState(() => {
        try {
            const stored = localStorage.getItem('luxvono_geo_context');
            if (stored) {
                const parsed = JSON.parse(stored);
                if (parsed.language === 'ar') return 'ar';
            }
        } catch (_) {}
        return 'ar'; // Default to Arabic as requested by the user
    });

    const isRtl = lang === 'ar';

    // Slide state for native app features carousel
    const [activeSlide, setActiveSlide] = useState(0);

    const toggleLanguage = () => {
        const nextLang = lang === 'ar' ? 'en' : 'ar';
        setLang(nextLang);
        try {
            const stored = localStorage.getItem('luxvono_geo_context');
            let parsed = stored ? JSON.parse(stored) : {};
            parsed.language = nextLang;
            localStorage.setItem('luxvono_geo_context', JSON.stringify(parsed));
            // Trigger global language update event
            window.dispatchEvent(new Event('languageChange'));
        } catch (_) {}
    };

    // Features to showcase with beautiful icons and text
    const features = [
        {
            icon: <Flame className="slide-icon" style={{ color: '#C5A880' }} size={32} />,
            title: {
                ar: "تسوق البث المباشر الفاخر",
                en: "Exclusive Live Stream Shopping"
            },
            desc: {
                ar: "شاهد عروض الأزياء الحية وتفاعل مع الخبراء مباشرة واشترِ أرقى الماركات بلمسة واحدة.",
                en: "Watch high-fidelity runway events, interact live with VIP consultants, and purchase instantly."
            }
        },
        {
            icon: <Volume2 className="slide-icon" style={{ color: '#C5A880' }} size={32} />,
            title: {
                ar: "غرف صوتية لكبار الشخصيات",
                en: "VIP Live Audio Rooms"
            },
            desc: {
                ar: "انضم إلى ندوات حصرية وتواصل مع مجتمع النخبة لمناقشة أحدث صيحات الموضة العالمية.",
                en: "Join private luxury discussions and network with elite collectors worldwide."
            }
        },
        {
            icon: <Award className="slide-icon" style={{ color: '#C5A880' }} size={32} />,
            title: {
                ar: "مساعد تسوق شخصي 1-على-1",
                en: "1-on-1 Concierge Consultation"
            },
            desc: {
                ar: "تواصل مع مستشارك الخاص عبر غرف دعم الصوت والفيديو لتحصل على تجربة تسوق مفصلة لك خصيصاً.",
                en: "Get tailored premium styling support directly from our dedicated concierge team."
            }
        }
    ];

    // Auto rotate feature slides
    useEffect(() => {
        const interval = setInterval(() => {
            setActiveSlide(prev => (prev + 1) % features.length);
        }, 5500);
        return () => clearInterval(interval);
    }, [features.length]);

    // Bilingual local translation dictionary
    const t = {
        title: {
            ar: "لوكسفونو - بوابة الفخامة",
            en: "LuxVono - The Luxury Portal"
        },
        subtitle: {
            ar: "حمّل التطبيق الرسمي واستمتع بأرقى تجارب التسوق المباشر والخدمات الحصرية لكبار الشخصيات.",
            en: "Download our official mobile application to experience live audio rooms, video commerce, and premium concierge service."
        },
        directDownload: {
            ar: "تحميل ملف APK مباشرة",
            en: "Download APK Directly"
        },
        apkDetails: {
            ar: "الإصدار الموحد الرسمي v2.0.0 (حديقة الورود • واقع افتراضي VR) • 77.0 ميجابايت",
            en: "Official Unified Build v2.0.0 (Rose Sanctuary • Spatial VR) • 77.0 MB"
        },
        comingSoon: {
            ar: "قريباً",
            en: "Coming Soon"
        },
        playStore: {
            ar: "متجر جوجل بلاي",
            en: "Google Play Store"
        },
        appStore: {
            ar: "متجر تطبيقات أبل",
            en: "Apple App Store"
        },
        secureNotice: {
            ar: "تطبيق لوكسفونو آمن تماماً ومعتمد ومحمي بأعلى معايير التشفير.",
            en: "The LuxVono application is completely secure, verified, and protected by leading encryption."
        },
        guideTitle: {
            ar: "خطوات التثبيت السهلة للأندرويد",
            en: "Easy Installation Steps for Android"
        },
        step1Title: {
            ar: "1. تحميل الملف",
            en: "1. Download the file"
        },
        step1Desc: {
            ar: "انقر فوق زر التحميل أعلاه لتنزيل ملف luxvono-app.apk الخاص بك.",
            en: "Click the download button above to download your luxvono-app.apk file."
        },
        step2Title: {
            ar: "2. السماح بالتثبيت",
            en: "2. Allow Installation"
        },
        step2Desc: {
            ar: "إذا طُلب منك، قم بتمكين 'تثبيت التطبيقات غير المعروفة' من إعدادات المتصفح أو الملفات.",
            en: "If prompted, enable 'Install Unknown Apps' from your browser or file manager settings."
        },
        step3Title: {
            ar: "3. ابدأ الاستكشاف",
            en: "3. Open & Launch"
        },
        step3Desc: {
            ar: "افتح التطبيق، وسجّل دخولك، واستمتع بتجربة تسوق لا مثيل لها.",
            en: "Open the app, sign in, and immerse yourself in the world of high-end luxury."
        },
        backToStore: {
            ar: "العودة إلى المتجر الرئيسي",
            en: "Go to Main Storefront"
        }
    };

    return (
        <div 
            className="download-page-root" 
            dir={isRtl ? 'rtl' : 'ltr'}
            style={{
                background: '#090A0F',
                color: '#FFFFFF',
                minHeight: '100vh',
                fontFamily: "'Outfit', 'Inter', 'Noto Sans Arabic', sans-serif",
                position: 'relative',
                overflowX: 'hidden',
                paddingBottom: '60px'
            }}
        >
            {/* Scoped CSS Injector */}
            <style dangerouslySetInnerHTML={{__html: `
                .download-page-root {
                    background: radial-gradient(circle at 50% -20%, #151824 0%, #090a0f 60%) !important;
                }
                /* Glow decorative orbs */
                .glow-orb-1 {
                    position: absolute;
                    width: 350px;
                    height: 350px;
                    background: radial-gradient(circle, rgba(197,168,128,0.12) 0%, rgba(197,168,128,0) 70%);
                    top: -50px;
                    left: -100px;
                    filter: blur(60px);
                    pointer-events: none;
                    z-index: 0;
                }
                .glow-orb-2 {
                    position: absolute;
                    width: 450px;
                    height: 450px;
                    background: radial-gradient(circle, rgba(123,97,255,0.08) 0%, rgba(123,97,255,0) 70%);
                    bottom: 10%;
                    right: -150px;
                    filter: blur(80px);
                    pointer-events: none;
                    z-index: 0;
                }
                /* Header controls */
                .download-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 24px 8%;
                    position: relative;
                    z-index: 10;
                }
                .logo-serif {
                    font-size: 24px;
                    font-weight: 700;
                    letter-spacing: 4px;
                    background: linear-gradient(135deg, #FFF 30%, #C5A880 100%);
                    -webkit-background-clip: text;
                    -webkit-text-fill-color: transparent;
                    text-shadow: 0 2px 20px rgba(197,168,128,0.2);
                }
                .lang-toggle-btn {
                    background: rgba(255, 255, 255, 0.04);
                    border: 1px solid rgba(255, 255, 255, 0.08);
                    border-radius: 40px;
                    padding: 8px 16px;
                    color: #FFFFFF;
                    font-size: 14px;
                    font-weight: 500;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
                }
                .lang-toggle-btn:hover {
                    background: rgba(197, 168, 128, 0.1);
                    border-color: rgba(197, 168, 128, 0.3);
                    transform: translateY(-2px);
                }
                /* Main layout container */
                .download-container {
                    max-width: 1200px;
                    margin: 0 auto;
                    padding: 20px 6%;
                    display: grid;
                    grid-template-columns: 1.1fr 0.9fr;
                    gap: 80px;
                    align-items: center;
                    position: relative;
                    z-index: 5;
                }
                @media (max-width: 968px) {
                    .download-container {
                        grid-template-columns: 1fr;
                        gap: 50px;
                        text-align: center;
                        padding-top: 10px;
                    }
                }
                /* Title and marketing */
                .hero-badge {
                    display: inline-flex;
                    align-items: center;
                    gap: 8px;
                    background: rgba(197, 168, 128, 0.08);
                    border: 1px solid rgba(197, 168, 128, 0.2);
                    padding: 6px 16px;
                    border-radius: 50px;
                    font-size: 13px;
                    font-weight: 600;
                    color: #C5A880;
                    letter-spacing: 1px;
                    margin-bottom: 24px;
                    text-transform: uppercase;
                }
                .hero-title {
                    font-size: clamp(32px, 5vw, 54px);
                    font-weight: 800;
                    line-height: 1.25;
                    margin-bottom: 20px;
                    letter-spacing: -0.5px;
                }
                .hero-desc {
                    font-size: 16px;
                    color: #A3A3A3;
                    line-height: 1.6;
                    margin-bottom: 40px;
                    max-width: 580px;
                }
                @media (max-width: 968px) {
                    .hero-desc {
                        margin-left: auto;
                        margin-right: auto;
                    }
                }
                /* Buttons and downloads */
                .download-panel-card {
                    background: rgba(13, 14, 22, 0.65);
                    border: 1px solid rgba(197, 168, 128, 0.15);
                    border-radius: 24px;
                    padding: 36px;
                    backdrop-filter: blur(25px);
                    box-shadow: 0 30px 60px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255,255,255,0.05);
                    position: relative;
                }
                .glow-border-highlight {
                    position: absolute;
                    top: -1px;
                    left: 20%;
                    right: 20%;
                    height: 2px;
                    background: linear-gradient(90deg, rgba(197,168,128,0) 0%, rgba(197,168,128,0.7) 50%, rgba(197,168,128,0) 100%);
                    pointer-events: none;
                }
                .apk-download-btn {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 16px;
                    background: linear-gradient(135deg, #C5A880 0%, #A28256 100%);
                    border: none;
                    border-radius: 16px;
                    padding: 18px 28px;
                    color: #000000;
                    font-size: 16px;
                    font-weight: 700;
                    width: 100%;
                    cursor: pointer;
                    text-decoration: none;
                    box-shadow: 0 15px 30px rgba(197, 168, 128, 0.25);
                    transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
                }
                .apk-download-btn:hover {
                    transform: translateY(-3px);
                    box-shadow: 0 20px 40px rgba(197, 168, 128, 0.4);
                }
                .apk-download-btn:active {
                    transform: translateY(-1px);
                }
                .apk-info-sub {
                    font-size: 13px;
                    color: #737373;
                    text-align: center;
                    margin-top: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 6px;
                }
                /* Grid of store badges */
                .store-badges-container {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 16px;
                    margin-top: 32px;
                    border-top: 1px solid rgba(255, 255, 255, 0.08);
                    padding-top: 32px;
                }
                @media (max-width: 480px) {
                    .store-badges-container {
                        grid-template-columns: 1fr;
                    }
                }
                .badge-card {
                    background: rgba(255, 255, 255, 0.02);
                    border: 1px solid rgba(255, 255, 255, 0.06);
                    border-radius: 16px;
                    padding: 16px;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    position: relative;
                    opacity: 0.85;
                    transition: all 0.3s ease;
                }
                .badge-card:hover {
                    background: rgba(255, 255, 255, 0.04);
                    border-color: rgba(255, 255, 255, 0.1);
                    opacity: 1;
                }
                .badge-status {
                    position: absolute;
                    top: -8px;
                    background: rgba(197, 168, 128, 0.12);
                    border: 1px solid rgba(197, 168, 128, 0.3);
                    color: #C5A880;
                    font-size: 9px;
                    font-weight: 700;
                    padding: 2px 8px;
                    border-radius: 20px;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                }
                .badge-title {
                    font-size: 14px;
                    font-weight: 600;
                    color: #E5E5E5;
                    margin-top: 6px;
                }
                .badge-subtitle {
                    font-size: 10px;
                    color: #525252;
                    margin-top: 2px;
                }
                /* Security banner */
                .security-bar {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    background: rgba(22, 101, 52, 0.06);
                    border: 1px solid rgba(22, 101, 52, 0.15);
                    border-radius: 14px;
                    padding: 12px 16px;
                    margin-top: 24px;
                    font-size: 12px;
                    color: #4ade80;
                    line-height: 1.4;
                }
                /* Feature dynamic slider */
                .feature-slider-panel {
                    margin-top: 40px;
                }
                .slide-card {
                    background: rgba(255,255,255,0.01);
                    border: 1px solid rgba(255,255,255,0.04);
                    border-radius: 18px;
                    padding: 24px;
                    min-height: 160px;
                    display: flex;
                    gap: 20px;
                    align-items: flex-start;
                    transition: all 0.5s ease;
                }
                .slide-icon-wrapper {
                    background: rgba(197, 168, 128, 0.08);
                    border: 1px solid rgba(197, 168, 128, 0.2);
                    border-radius: 12px;
                    padding: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
                .slide-content-text h4 {
                    font-size: 17px;
                    font-weight: 700;
                    color: #FFFFFF;
                    margin-bottom: 8px;
                }
                .slide-content-text p {
                    font-size: 13px;
                    color: #A3A3A3;
                    line-height: 1.5;
                }
                .slide-dots {
                    display: flex;
                    gap: 8px;
                    justify-content: center;
                    margin-top: 16px;
                }
                .dot {
                    width: 8px;
                    height: 8px;
                    border-radius: 50%;
                    background: rgba(255,255,255,0.15);
                    cursor: pointer;
                    transition: all 0.3s ease;
                }
                .dot.active {
                    background: #C5A880;
                    width: 24px;
                    border-radius: 4px;
                }
                /* Steps Guide Section */
                .steps-section {
                    max-width: 1200px;
                    margin: 80px auto 0 auto;
                    padding: 0 6%;
                    position: relative;
                    z-index: 5;
                }
                .section-header-title {
                    font-size: 28px;
                    font-weight: 800;
                    text-align: center;
                    margin-bottom: 48px;
                    letter-spacing: -0.5px;
                }
                .steps-grid {
                    display: grid;
                    grid-template-columns: 1fr 1fr 1fr;
                    gap: 24px;
                }
                @media (max-width: 850px) {
                    .steps-grid {
                        grid-template-columns: 1fr;
                        gap: 32px;
                    }
                }
                .step-card {
                    background: rgba(13, 14, 22, 0.4);
                    border: 1px solid rgba(255, 255, 255, 0.05);
                    border-radius: 20px;
                    padding: 30px;
                    transition: all 0.3s ease;
                }
                .step-card:hover {
                    border-color: rgba(197, 168, 128, 0.25);
                    transform: translateY(-4px);
                    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
                }
                .step-number-glow {
                    font-size: 14px;
                    font-weight: 700;
                    color: #C5A880;
                    text-transform: uppercase;
                    margin-bottom: 16px;
                    display: inline-block;
                    letter-spacing: 1px;
                }
                .step-title {
                    font-size: 18px;
                    font-weight: 700;
                    margin-bottom: 12px;
                    color: #FFFFFF;
                }
                .step-desc {
                    font-size: 14px;
                    color: #8C8C8C;
                    line-height: 1.6;
                }
                /* Global Footer styling inside download page context */
                .back-storefront-wrapper {
                    text-align: center;
                    margin-top: 60px;
                }
                .back-storefront-btn {
                    display: inline-flex;
                    align-items: center;
                    gap: 8px;
                    background: transparent;
                    border: 1px solid rgba(255, 255, 255, 0.15);
                    padding: 12px 24px;
                    border-radius: 50px;
                    color: #A3A3A3;
                    font-size: 14px;
                    font-weight: 500;
                    cursor: pointer;
                    text-decoration: none;
                    transition: all 0.3s ease;
                }
                .back-storefront-btn:hover {
                    border-color: #C5A880;
                    color: #FFFFFF;
                    background: rgba(197, 168, 128, 0.05);
                }
            `}} />

            {/* Glowing background decorators */}
            <div className="glow-orb-1"></div>
            <div className="glow-orb-2"></div>

            {/* Premium Header Controls */}
            <header className="download-header">
                <div className="logo-serif">LUXVONO</div>
                <button className="lang-toggle-btn" onClick={toggleLanguage} aria-label="Toggle language">
                    <Languages size={16} style={{ color: '#C5A880' }} />
                    <span>{lang === 'ar' ? 'English' : 'العربية'}</span>
                </button>
            </header>

            {/* Main Content Layout */}
            <main className="download-container">
                {/* Column 1: Marketing & Features */}
                <div className="hero-marketing-pane">
                    <span className="hero-badge">
                        <Smartphone size={14} style={{ transform: isRtl ? 'scaleX(-1)' : 'none' }} />
                        {isRtl ? "تطبيق الجوال الموحد" : "Unified Mobile Client"}
                    </span>
                    <h1 className="hero-title">
                        {isRtl ? (
                            <>
                                ارتقِ بتجربتك مع <span style={{ color: '#C5A880' }}>تطبيق لوكسفونو</span>
                            </>
                        ) : (
                            <>
                                Elevate Commerce with <span style={{ color: '#C5A880' }}>LuxVono App</span>
                            </>
                        )}
                    </h1>
                    <p className="hero-desc">
                        {isRtl ? t.subtitle.ar : t.subtitle.en}
                    </p>

                    {/* Interactive Showcase slider */}
                    <div className="feature-slider-panel">
                        <div className="slide-card">
                            <div className="slide-icon-wrapper">
                                {features[activeSlide].icon}
                            </div>
                            <div className="slide-content-text">
                                <h4>{isRtl ? features[activeSlide].title.ar : features[activeSlide].title.en}</h4>
                                <p>{isRtl ? features[activeSlide].desc.ar : features[activeSlide].desc.en}</p>
                            </div>
                        </div>
                        <div className="slide-dots">
                            {features.map((_, idx) => (
                                <span 
                                    key={idx} 
                                    className={`dot ${activeSlide === idx ? 'active' : ''}`}
                                    onClick={() => setActiveSlide(idx)}
                                />
                            ))}
                        </div>
                    </div>
                </div>

                {/* Column 2: Premium Download Box */}
                <div className="download-card-pane">
                    <div className="download-panel-card">
                        <div className="glow-border-highlight"></div>

                        {/* Direct Downloader Link */}
                        <a href="/media/luxvono-app.apk?v=202610182238" className="apk-download-btn" download="luxvono-app.apk">
                            <Download size={22} />
                            <span>{isRtl ? t.directDownload.ar : t.directDownload.en}</span>
                        </a>

                        <div className="apk-info-sub">
                            <ShieldCheck size={14} style={{ color: '#C5A880' }} />
                            <span>{isRtl ? t.apkDetails.ar : t.apkDetails.en}</span>
                        </div>

                        {/* Secure Notice Banner */}
                        <div className="security-bar">
                            <ShieldCheck size={18} style={{ flexShrink: 0 }} />
                            <span>{isRtl ? t.secureNotice.ar : t.secureNotice.en}</span>
                        </div>

                        {/* Store Badges Panel */}
                        <div className="store-badges-container">
                            {/* Play Store */}
                            <div className="badge-card">
                                <span className="badge-status">{isRtl ? t.comingSoon.ar : t.comingSoon.en}</span>
                                <Smartphone size={22} style={{ color: '#737373' }} />
                                <span className="badge-title">{isRtl ? t.playStore.ar : t.playStore.en}</span>
                                <span className="badge-subtitle">Android Devices</span>
                            </div>

                            {/* App Store */}
                            <div className="badge-card">
                                <span className="badge-status">{isRtl ? t.comingSoon.ar : t.comingSoon.en}</span>
                                <Smartphone size={22} style={{ color: '#737373' }} />
                                <span className="badge-title">{isRtl ? t.appStore.ar : t.appStore.en}</span>
                                <span className="badge-subtitle">iOS Devices</span>
                            </div>
                        </div>
                    </div>
                </div>
            </main>

            {/* Step-by-Step Installation Walkthrough Section */}
            <section className="steps-section">
                <h2 className="section-header-title">
                    {isRtl ? t.guideTitle.ar : t.guideTitle.en}
                </h2>

                <div className="steps-grid">
                    {/* Step 1 */}
                    <div className="step-card">
                        <span className="step-number-glow">{isRtl ? "الخطوة الأولى" : "Step 01"}</span>
                        <h3 className="step-title">{isRtl ? t.step1Title.ar : t.step1Title.en}</h3>
                        <p className="step-desc">{isRtl ? t.step1Desc.ar : t.step1Desc.en}</p>
                    </div>

                    {/* Step 2 */}
                    <div className="step-card">
                        <span className="step-number-glow">{isRtl ? "الخطوة الثانية" : "Step 02"}</span>
                        <h3 className="step-title">{isRtl ? t.step2Title.ar : t.step2Title.en}</h3>
                        <p className="step-desc">{isRtl ? t.step2Desc.ar : t.step2Desc.en}</p>
                    </div>

                    {/* Step 3 */}
                    <div className="step-card">
                        <span className="step-number-glow">{isRtl ? "الخطوة الثالثة" : "Step 03"}</span>
                        <h3 className="step-title">{isRtl ? t.step3Title.ar : t.step3Title.en}</h3>
                        <p className="step-desc">{isRtl ? t.step3Desc.ar : t.step3Desc.en}</p>
                    </div>
                </div>

                {/* Back to store navigation links */}
                <div className="back-storefront-wrapper">
                    <a href="/" className="back-storefront-btn">
                        {isRtl ? (
                            <>
                                <ChevronRight size={16} style={{ marginLeft: '4px', transform: 'scaleX(-1)' }} />
                                <span>{t.backToStore.ar}</span>
                            </>
                        ) : (
                            <>
                                <span>{t.backToStore.en}</span>
                                <ArrowRight size={16} style={{ marginLeft: '4px' }} />
                            </>
                        )}
                    </a>
                </div>
            </section>
        </div>
    );
};

export default DownloadPage;
